<template>
  <div>
    <portal-breadcrumbs v-if="!showInModal">
      <span>
        <span class="aLink mr-1" @click="$router.push('/redirect/settings')">
          <i class="fas fa-cogs"></i>
          {{ t("sidebar.settings.title") }}</span
        >/
        <span>{{ t("views.SettingsEntry.headlines.main") }}</span>
        /

        <span class="aLink" @click="$router.push('/technicalPlaces')">{{
          t("views.ih.Category.technicalPlaces")
        }}</span>
        /
        <strong>{{ t("views.ih.Category.updateTechnicalPlace") }} </strong>
      </span>
    </portal-breadcrumbs>
    <InputFrame
      :title="t('views.ih.Category.createNewTechnicalPlace')"
      :loading="loading"
      :display-mode="showInModal ? InputFrameMode.modal : undefined"
      create
      :submit="
        async () => {
          await createTechnicalPlace();
          return { success: true };
        }
      "
      @cancel="cancel"
    >
      <InputRow
        v-if="props.siteId == null"
        :model-value="siteIdRef"
        :display-name="t('global.site')"
        name="site"
        required
      >
        <template #default="{ field: inputField }">
          <SearchSelect
            v-bind="inputField"
            v-model="siteIdRef"
            :mode="SelectMode.SITE"
            :required="true"
            class="w-100"
          />
        </template>
      </InputRow>
      <TPComponent :technical-place="technicalPlace" :site-id="siteIdRef" />

      <template #frame-ok>
        <i class="fas fa-plus mr-2" />{{ t("views.ih.Category.createNewTechnicalPlace") }}
      </template>
    </InputFrame>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, type PropType } from "vue";
import { useRouter } from "vue-router";
import { useOGTechnicalPlaceCreateMutation, type OGTechnicalPlaceCreateMutation } from "@/graphql";
import TPComponent from "../components/TPComponent.vue";
import InputFrame from "@/components/shared/form/InputFrame.vue";
import type { TechnicalPlaceEditType } from "../helper/CR_TP_Type";
import usePropsAsDefault from "@/functions/usePropsAsDefault";
import { InputFrameMode } from "@/components/shared/form/InputFrameMode";
import InputRow from "@/components/shared/form/InputRow.vue";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";

const props = defineProps({
  showInModal: {
    type: Boolean,
    required: false,
    default: false,
  },
  defaultTechnicalPlace: {
    type: Object as PropType<{ name: string }>,
    required: false,
    default: undefined,
  },
  siteId: {
    type: Number,
    required: false,
    default: undefined,
  },
});

const siteIdRef = usePropsAsDefault({
  dynamicProps: computed(() => props.siteId),
  replace: true,
});

const emit = defineEmits<{
  created: [costCeter: OGTechnicalPlaceCreateMutation["OGTechnicalPlaceCreate"]];
  cancel: [];
}>();
const router = useRouter();

function getDefaultTechnicalPlace(
  predefined?: Partial<TechnicalPlaceEditType>,
): TechnicalPlaceEditType {
  return {
    ...(predefined ?? {}),
    name: "",
    description: "",
    path: "",
    code: "",
    parentId: undefined,
  };
}

const technicalPlace = usePropsAsDefault({
  dynamicProps: computed(() => getDefaultTechnicalPlace(props.defaultTechnicalPlace)),
  replace: true,
});

const { mutate: createTechnicalPlaceMutation, loading } = useOGTechnicalPlaceCreateMutation({});

async function createTechnicalPlace() {
  const result = await createTechnicalPlaceMutation(
    {
      technicalPlace: {
        name: technicalPlace.value.name,
        costCenterId: technicalPlace.value.costCenterId!,
        description: technicalPlace.value.description,
        code: technicalPlace.value.code,
        parentId: technicalPlace.value.parentId,
      },
    },
    {
      refetchQueries: ["TechnicalPlacesForSelect"],
    },
  );

  if (props.showInModal) {
    if (result?.data?.OGTechnicalPlaceCreate != null) {
      emit("created", result?.data?.OGTechnicalPlaceCreate);
    }
  } else {
    router.back();
  }
}

function cancel() {
  if (!props.showInModal) {
    router.back();
  } else {
    emit("cancel");
  }
}

const { t } = useTranslation();
</script>
